import firebase from '@config/firebase.config';
import { toDateValue } from '@/utils/firestoreDate';

const database = firebase.firestore();

/**
 * Fetch all invoices for a company, newest first.
 * No cloud function backs this yet, so it reads companyDatabase/{companyId}/
 * invoices directly — the same collection functions/razorpay-api.js writes
 * to from Razorpay webhooks.
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Invoice documents, each with its Firestore doc id
 */
async function fetchCompanyInvoices(companyId) {
  const snapshot = await database
    .collection('companyDatabase')
    .doc(companyId)
    .collection('invoices')
    .get();

  const invoices = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
  invoices.sort((a, b) => {
    const aTime = toDateValue(a.createdAt)?.getTime() || 0;
    const bTime = toDateValue(b.createdAt)?.getTime() || 0;
    return bTime - aTime;
  });
  return invoices;
}

/**
 * Format an amount stored in the smallest currency unit (paise/cents) as a
 * localized currency string.
 * @param {number} amount - Amount in the smallest currency unit
 * @param {string} currency - Currency code (default: 'inr')
 * @returns {string} Formatted currency string
 */
function formatCurrency(amount, currency = 'inr') {
  try {
    const value = (amount || 0) / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${(amount / 100).toFixed(2)}`;
  }
}

export default {
  fetchCompanyInvoices,
  formatCurrency,
};
